import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useCourseDetailModal, CourseDetailModal } from '../../components/CourseDetailModal';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowRight, Bot, Users, Award, Cpu, Wrench, MessageSquare, ChevronDown,
  ExternalLink, Send, CheckCircle2, Rocket, GraduationCap, Shield, Brain,
  Trophy, Sparkles, CircuitBoard, Eye, Cog, Factory, Briefcase, Handshake,
  Globe, Check, BarChart3, Gamepad2, Navigation, Target, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* ───── Inline Contact Form ───── */
function InlineContactForm({ program }) {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', studentGrade: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) { toast.error('Name and email required.'); return; }
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/contact/`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email, phone: formData.phone, subject: `${program} Interest`, message: `Program: ${program}\nGrade: ${formData.studentGrade}` }),
      });
    } catch {}
    setSubmitted(true); setSubmitting(false);
    toast.success("We received your interest! We'll be in touch soon.");
  };

  if (submitted) return (
    <div className="text-center py-3" data-testid={`${program.toLowerCase().replace(/\s+/g, '-')}-form-success`}>
      <CheckCircle2 className="w-7 h-7 text-emerald-400 mx-auto mb-1" />
      <p className="text-xs font-semibold text-white">Received! We'll be in touch.</p>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="space-y-2" data-testid={`${program.toLowerCase().replace(/\s+/g,'-')}-contact-form`}>
      <div className="grid grid-cols-2 gap-2">
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Name *" value={formData.name} onChange={e => setFormData(p=>({...p, name: e.target.value}))} required />
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" type="email" placeholder="Email *" value={formData.email} onChange={e => setFormData(p=>({...p, email: e.target.value}))} required />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Phone" value={formData.phone} onChange={e => setFormData(p=>({...p, phone: e.target.value}))} />
        <Input className="bg-white/10 border-white/15 text-white placeholder:text-white/30 text-sm h-8" placeholder="Student Grade" value={formData.studentGrade} onChange={e => setFormData(p=>({...p, studentGrade: e.target.value}))} />
      </div>
      <Button type="submit" disabled={submitting} className="w-full bg-rsc-yellow text-rsc-navy font-bold rounded-full h-8 text-xs">
        {submitting ? 'Sending...' : <><Send className="w-3 h-3 mr-1" /> Express Interest</>}
      </Button>
    </form>
  );
}


/* ───── Themed Robotics Courses ───── */
const THEMED_CATEGORIES = [
  { id: 'robot-builders', theme: 'Robot Builders', tagline: 'Build & Program', grades: 'Gr K–5',
    icon: Bot, color: 'from-emerald-500 to-teal-400', colorAccent: 'text-emerald-300', bgAccent: 'bg-emerald-500/15 border-emerald-400/20',
    tags: ['LEGO', 'Sensors', 'Autonomous'],
    highlights: ['Build your first LEGO robot with sensors and motors', 'Program autonomous path navigation', 'Complete challenge missions in teams', 'Foundation for FLL competitions'],
    description: 'Build your first LEGO robot, add sensors and motors, then program it to follow a path and complete missions autonomously! The perfect introduction to competitive robotics.' },
  { id: 'maze-runners', theme: 'Coding Olympics: The Robot Games', tagline: 'Navigate & Solve', grades: 'Gr 3–8',
    icon: Navigation, color: 'from-blue-500 to-indigo-400', colorAccent: 'text-blue-300', bgAccent: 'bg-blue-500/15 border-blue-400/20',
    tags: ['Maze', 'Algorithms', 'Speed'],
    highlights: ['Program a robot to navigate a maze without human help', 'Use sensors to detect walls and make turns', 'Optimize paths for speed competitions', 'Learn wall-following algorithms'],
    description: 'Program a robot to navigate a maze without human help! Use sensors to detect walls, make turns, and find the exit. Compete for the fastest maze-solving time.' },
  { id: 'sumo-bots', theme: 'Kinetic Creators: The Responsive Model Lab', tagline: 'Design & Compete', grades: 'Gr 3–8',
    icon: Shield, color: 'from-red-500 to-orange-400', colorAccent: 'text-red-300', bgAccent: 'bg-red-500/15 border-red-400/20',
    tags: ['Strategy', 'Competition', 'Design'],
    highlights: ['Design and build a sumo wrestling robot', 'Push opponents out of the ring!', 'Strategy, strength, and smart programming', 'Tournament bracket competition'],
    description: 'Design and build a sumo robot that pushes opponents out of the ring! Strategy, strength, and smart programming all matter in this head-to-head competition.' },
  { id: 'line-followers', theme: 'Glow-Botics: Build and Code a Smart RGB Lamp', tagline: 'Light Design & Code', grades: 'Gr K–8',
    icon: Target, color: 'from-amber-500 to-yellow-400', colorAccent: 'text-amber-300', bgAccent: 'bg-amber-500/15 border-amber-400/20',
    tags: ['RGB LEDs', 'Color', 'Microcontroller'],
    highlights: ['Build a color-changing RGB lamp', 'Program custom lighting sequences', 'Learn color theory and light mixing', 'Control with buttons or sensors'],
    description: 'Build your own smart RGB lamp and program it to change colors, fade, pulse, and react to your commands! Learn the intersection of light design, color theory, and coding.' },
  { id: 'robot-rescue', theme: 'Rescue Bot: Obstacle Course Mission', tagline: 'Navigate & Retrieve', grades: 'Gr 3–8',
    icon: Zap, color: 'from-cyan-500 to-sky-400', colorAccent: 'text-cyan-300', bgAccent: 'bg-cyan-500/15 border-cyan-400/20',
    tags: ['Obstacles', 'Gripper', 'Multi-Sensor'],
    highlights: ['Navigate an obstacle course autonomously', 'Climb ramps and avoid hazards', 'Pick up objects with gripper mechanisms', 'Timed rescue mission optimization'],
    description: 'Program your robot to navigate an obstacle course — climb ramps, avoid hazards, pick up objects, and reach the finish line! Multi-sensor integration challenge.' },
  { id: 'drone-basics', theme: 'Eco-Bot: Build a Smart Recycling Robot', tagline: 'Sort & Automate', grades: 'Gr 5–12',
    icon: Rocket, color: 'from-violet-500 to-purple-400', colorAccent: 'text-violet-300', bgAccent: 'bg-violet-500/15 border-violet-400/20',
    tags: ['Sensors', 'Automation', 'Sustainability'],
    highlights: ['Build an automated recycling sorter', 'Use sensors to detect different materials', 'Program sorting logic and conveyor systems', 'Solve real-world environmental challenges'],
    description: 'Build a smart robot that automatically sorts recyclables! Use sensors to detect materials, program sorting logic, and create an automated system that helps the environment.' },
  { id: 'battle-bots', theme: 'Battle Bots: Design, Build & Compete', tagline: 'Build & Battle', grades: 'Gr 5–12',
    icon: Gamepad2, color: 'from-rose-500 to-pink-400', colorAccent: 'text-rose-300', bgAccent: 'bg-rose-500/15 border-rose-400/20',
    tags: ['Engineering', 'Combat', 'Durability'],
    highlights: ['Engineer robots with offensive and defensive features', 'Flippers, spinners, wedges — choose your strategy', 'Build for durability under pressure', 'Compete head-to-head in bracket tournament'],
    description: 'Engineer a competition robot with offensive and defensive features, then compete head-to-head in arena challenges! Design, build, and battle.' },
  { id: 'smart-robots', theme: 'AI Robots: Smart Mission Lab', tagline: 'AI & Vision', grades: 'Gr 5–12',
    icon: Brain, color: 'from-fuchsia-500 to-pink-400', colorAccent: 'text-fuchsia-300', bgAccent: 'bg-fuchsia-500/15 border-fuchsia-400/20',
    tags: ['AI', 'Vision', 'Autonomous'],
    highlights: ['Teach your robot to recognize colors and objects', 'Make decisions using basic AI concepts', 'Integrate cameras and classifiers', 'Build truly autonomous robot behaviors'],
    description: 'Teach your robot to recognize colors, avoid objects, and make decisions using basic AI — robots that think for themselves! The frontier of educational robotics.' },
];

/* ───── Pathway Data (core progression, competition-agnostic) ───── */
const PATHWAY_LEVELS = [
  {
    id: 'explorer', level: 1, title: 'Explorer', subtitle: 'Elementary · K–5', ages: 'Ages 6–10',
    color: 'from-emerald-500 to-teal-400', colorAccent: 'text-emerald-300', bgAccent: 'bg-emerald-500/15 border-emerald-400/20',
    icon: Rocket,
    highlights: ['LEGO WeDo 2.0 & SPIKE Essential', 'Scratch Jr & visual block programming', 'Simple machines — gears, pulleys, levers', 'Intro to sensors & autonomous movement', 'AI: pattern recognition with Teachable Machine'],
    milestones: ['Build & program a robot for 3 autonomous tasks', 'Present a team innovation project', 'Earn the RSS Explorer Badge'],
    competition: 'FLL Explore or VEX IQ (Elementary)',
    credential: 'Explorer Badge',
  },
  {
    id: 'builder', level: 2, title: 'Builder', subtitle: 'Middle School · 6–8', ages: 'Ages 10–14',
    color: 'from-blue-500 to-indigo-400', colorAccent: 'text-blue-300', bgAccent: 'bg-blue-500/15 border-blue-400/20',
    icon: Cog,
    highlights: ['LEGO SPIKE Prime — complex builds & sensors', 'Intro to Python programming', '3D design with TinkerCAD & Onshape', 'Electronics fundamentals with Arduino', 'AI: ML for Kids — build classifiers'],
    milestones: ['Compete in regional qualifier (FLL or VEX IQ)', 'Write Python programs with sensor input', 'Design & 3D-print a custom robot part'],
    competition: 'FLL Challenge or VEX IQ (Competition)',
    credential: 'Builder Certificate',
  },
  {
    id: 'engineer', level: 3, title: 'Engineer', subtitle: 'High School · 9–12', ages: 'Ages 14–18',
    color: 'from-orange-500 to-amber-400', colorAccent: 'text-orange-300', bgAccent: 'bg-orange-500/15 border-orange-400/20',
    icon: Wrench,
    highlights: ['CAD with Onshape / SolidWorks', 'Java, Python, C++ programming', 'FANUC robot operation & safety', 'PID control & path planning', 'AI: OpenCV computer vision for autonomous scoring'],
    milestones: ['Compete at regional level (FTC/FRC or VEX V5)', 'Earn FANUC Handling Tool Operator Certificate', 'Build a CAD portfolio of 3+ subsystems'],
    competition: 'FTC / FRC or VEX V5',
    credential: 'FANUC Handling Tool Cert.',
  },
  {
    id: 'professional', level: 4, title: 'Professional', subtitle: 'Post-Secondary & Workforce', ages: 'Ages 18+',
    color: 'from-purple-500 to-violet-400', colorAccent: 'text-purple-300', bgAccent: 'bg-purple-500/15 border-purple-400/20',
    icon: Briefcase,
    highlights: ['Advanced FANUC certs (iRVision, multi-process)', 'ROS 2 — industry-standard robot middleware', 'Deep learning & reinforcement learning', 'LLM-driven robot task planning', 'Capstone project + internship pipeline'],
    milestones: ['Earn 2–3 additional FANUC certifications', 'Complete a functional ROS2 robot project', 'Demonstrate AI integration in robotics', 'Secure internship or job placement'],
    competition: 'VEX U / Open Competitions',
    credential: 'FANUC Multi-Cert + Placement',
  },
];

const HERO_STATS = [
  { value: '7', label: 'FANUC Industrial Robots', icon: Factory },
  { value: '$80M+', label: 'FIRST Scholarships Annually', icon: Award },
  { value: 'K–Career', label: 'Complete Pipeline', icon: ArrowRight },
  { value: '12,000+', label: 'Learner Network', icon: Users },
];

/* ───── Main Page ───── */
export default function RoboticsHubPage() {
  const [expandedLevel, setExpandedLevel] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [activeForm, setActiveForm] = useState(null);
  const [courseIds, setCourseIds] = useState({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && hash !== 'categories' && hash !== 'courses') {
      setExpandedCategory(hash);
      setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 400);
    }
  }, [location.hash]);

  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    axios.get(`${API_URL}/api/courses/?page_size=5000`).then(res => {
      const courses = res.data?.results || res.data || [];
      const ids = {};
      courses.filter(c => c.category === 'robotics').forEach(c => { ids[c.title] = c.id; });
      setCourseIds(ids);
    }).catch(() => {});
  }, []);

  const courseModal = useCourseDetailModal();

  return (
    <div data-testid="robotics-hub-page">

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative min-h-[78vh] flex items-center overflow-hidden bg-gradient-to-br from-rsc-navy via-[#1a3a6d] to-[#0f2847]">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'linear-gradient(rgba(251,191,36,0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(251,191,36,0.2) 1px, transparent 1px)', backgroundSize: '50px 50px' }} />
        <div className="absolute top-20 -right-40 w-[500px] h-[500px] bg-orange-500/8 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 -left-40 w-[400px] h-[400px] bg-blue-500/10 rounded-full blur-[100px]" />

        <div className="container-app relative z-10 py-12">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-sm font-medium mb-4" data-testid="hero-badge">
              <CircuitBoard className="w-4 h-4" /> AI-Powered Robotics Education
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.1] mb-4 tracking-tight">
              Build the Future.<br /><span className="text-rsc-yellow">One Robot at a Time.</span>
            </h1>
            <p className="text-base sm:text-lg text-white/60 max-w-xl mb-7 leading-relaxed">
              From first LEGO builds at age 6 to FANUC-certified careers — the most comprehensive robotics pipeline in the greater Chicago region.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              <a href="#courses">
                <Button className="bg-rsc-yellow text-rsc-navy text-sm px-7 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20 hover:shadow-rsc-yellow/30 hover:scale-[1.02] transition-all" data-testid="explore-courses-btn">
                  Explore Courses <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
              <a href="#pathway">
                <Button variant="outline" className="border-white/25 text-white hover:bg-white/10 text-sm px-7 py-5 rounded-full" data-testid="view-pathway-btn">
                  View Full Pathway
                </Button>
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hero-stats">
              {HERO_STATS.map((stat, i) => (
                <div key={i} className="p-3 rounded-xl bg-white/[0.06] border border-white/[0.08]">
                  <stat.icon className="w-4 h-4 text-rsc-yellow/60 mb-1" />
                  <p className="text-xl sm:text-2xl font-extrabold text-white leading-none">{stat.value}</p>
                  <p className="text-[11px] text-white/40 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-10 animate-bounce">
          <ChevronDown className="w-5 h-5 text-white/20" />
        </div>
      </section>

      {/* ═══════════ ROBOTICS COURSES ═══════════ */}
      <section id="categories" className="relative py-12 bg-gradient-to-b from-[#0f2847] to-rsc-navy" data-testid="themed-categories-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" /> ROBOTICS COURSES
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              8 Ways to Build <span className="text-rsc-yellow">the Future</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm">
              From your first LEGO build to AI-powered autonomous robots. Each course includes hands-on kits and competition preparation.
            </p>
          </div>

          <div className="space-y-3">
            {THEMED_CATEGORIES.map((cat) => {
              const Icon = cat.icon;
              const isExpanded = true;
              return (
                <div key={cat.id} id={cat.id} data-testid={`theme-${cat.id}`}>
                  <div className={`rounded-xl border transition-all border-white/15 bg-white/[0.05]`}
                    >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${cat.color} flex items-center justify-center shadow-lg flex-shrink-0`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h3 className="text-lg font-bold text-white">{cat.theme}</h3>
                              <p className={`text-xs ${cat.colorAccent} font-medium`}>{cat.tagline} · {cat.grades}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <div className="hidden sm:flex gap-1">
                                {cat.tags.map(t => <Badge key={t} className={`${cat.bgAccent} ${cat.colorAccent} text-[10px]`}>{t}</Badge>)}
                              </div>
                              <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          {!isExpanded && <p className="text-xs text-white/35 mt-1.5 line-clamp-1">{cat.description}</p>}
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="mt-5 ml-0 sm:ml-[60px] space-y-4" data-testid={`theme-detail-${cat.id}`}>
                          <p className="text-sm text-white/50 leading-relaxed">{cat.description}</p>
                          <div>
                            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">What You'll Learn</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1">
                              {cat.highlights.map((h, j) => (
                                <div key={j} className="flex items-start gap-1.5">
                                  <Check className={`w-3.5 h-3.5 ${cat.colorAccent} flex-shrink-0 mt-0.5`} />
                                  <span className="text-xs text-white/50">{h}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <Button size="sm" className={`bg-gradient-to-r ${cat.color} text-white font-bold rounded-full px-5 cursor-pointer`} onClick={() => courseModal.openCourseDetail(cat.theme)}>
                              Course Details <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                            </Button>
                            {activeForm !== cat.id ? (
                              <Button size="sm" variant="outline" className="border-white/15 text-white/60 hover:bg-white/5 rounded-full px-5" onClick={(e) => { e.stopPropagation(); setActiveForm(cat.id); }}>
                                <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> I'm Interested
                              </Button>
                            ) : (
                              <div className="flex-1 max-w-sm" onClick={e => e.stopPropagation()}>
                                <InlineContactForm program={cat.theme} />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ AI + ROBOTICS VISION ═══════════ */}
      <section className="relative py-12 bg-gradient-to-b from-[#0f2847] to-rsc-navy" data-testid="ai-robotics-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-purple-400/30 bg-purple-500/10 text-purple-300 text-xs font-medium mb-3">
              <Brain className="w-3.5 h-3.5" /> THE FUTURE IS HERE
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              Where AI Meets <span className="text-rsc-yellow">Robotics</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm">
              AI integrated at every level — from pattern recognition in elementary to deep learning and LLM-driven robot control.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              { level: 'Explorer', icon: Sparkles, skill: 'Pattern Recognition', desc: 'Google Teachable Machine — train models to recognize objects and trigger robot actions', tools: 'Teachable Machine, Scratch' },
              { level: 'Builder', icon: BarChart3, skill: 'Data & Classification', desc: 'Build ML classifiers that control micro:bit and SPIKE behaviors in real-time', tools: 'ML for Kids, Python' },
              { level: 'Engineer', icon: Eye, skill: 'Computer Vision', desc: 'OpenCV vision pipelines for autonomous scoring — AprilTag detection, object tracking', tools: 'OpenCV, TensorFlow' },
              { level: 'Professional', icon: Brain, skill: 'Deep Learning & LLMs', desc: 'YOLO object detection on FANUC, reinforcement learning, LLM-driven task planning', tools: 'PyTorch, ROS2, Isaac' },
            ].map((item, i) => (
              <div key={i} className="p-5 rounded-xl bg-white/[0.04] border border-white/[0.08] hover:bg-white/[0.07] transition-colors">
                <div className="w-9 h-9 rounded-lg bg-rsc-yellow/10 flex items-center justify-center mb-3">
                  <item.icon className="w-4.5 h-4.5 text-rsc-yellow" />
                </div>
                <Badge className="bg-white/10 text-white/60 border-white/10 text-[10px] mb-2">{item.level}</Badge>
                <h3 className="text-base font-bold text-white mb-1">{item.skill}</h3>
                <p className="text-xs text-white/45 leading-relaxed mb-2">{item.desc}</p>
                <p className="text-[10px] text-white/25 font-mono">{item.tools}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ THE PATHWAY (core skills, competition-agnostic) ═══════════ */}
      <section id="pathway" className="relative py-12 bg-rsc-navy" data-testid="pathway-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-emerald-400/30 bg-emerald-500/10 text-emerald-300 text-xs font-medium mb-3">
              <Rocket className="w-3.5 h-3.5" /> K-12 TO CAREER
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              The Robotics <span className="text-emerald-400">Pathway</span>
            </h2>
            <p className="text-white/50 max-w-xl mx-auto text-sm">
              Core skills progression from first builds to industry certifications. At each level, learners choose a competition track (FIRST or VEX).
            </p>
          </div>

          <div className="space-y-3">
            {PATHWAY_LEVELS.map((level) => {
              const Icon = level.icon;
              const isExpanded = expandedLevel === level.id;
              return (
                <div key={level.id} data-testid={`pathway-level-${level.id}`}>
                  <div
                    className={`rounded-xl border transition-all border-white/15 bg-white/[0.05]`}
                    onClick={() => setExpandedLevel(prev => prev === level.id ? null : level.id)}
                  >
                    <div className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${level.color} flex items-center justify-center shadow-lg`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-center text-[10px] text-white/25 mt-1 font-mono">LVL {level.level}</p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3">
                            <div>
                              <h3 className="text-lg font-bold text-white">{level.title}</h3>
                              <p className={`text-xs ${level.colorAccent} font-medium`}>{level.subtitle} · {level.ages}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className={`${level.bgAccent} ${level.colorAccent} text-[10px] hidden sm:flex`}>{level.credential}</Badge>
                              <ChevronDown className={`w-4 h-4 text-white/30 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {level.highlights.slice(0, 3).map((h, j) => (
                              <span key={j} className="text-[10px] text-white/35 bg-white/[0.04] px-2 py-0.5 rounded-full">{h}</span>
                            ))}
                            {!isExpanded && level.highlights.length > 3 && <span className="text-[10px] text-white/20">+{level.highlights.length - 3} more</span>}
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="mt-5 ml-0 sm:ml-[60px] space-y-4" data-testid={`pathway-detail-${level.id}`}>
                          <div>
                            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Core Curriculum</h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                              {level.highlights.map((h, j) => (
                                <div key={j} className="flex items-start gap-1.5">
                                  <Check className={`w-3.5 h-3.5 ${level.colorAccent} flex-shrink-0 mt-0.5`} />
                                  <span className="text-xs text-white/50">{h}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div>
                            <h4 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Key Milestones</h4>
                            <div className="space-y-1">
                              {level.milestones.map((m, j) => (
                                <div key={j} className="flex items-start gap-1.5">
                                  <Trophy className={`w-3.5 h-3.5 ${level.colorAccent} flex-shrink-0 mt-0.5`} />
                                  <span className="text-xs text-white/50">{m}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center gap-3 flex-wrap">
                            <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${level.bgAccent}`}>
                              <GraduationCap className={`w-3.5 h-3.5 ${level.colorAccent}`} />
                              <span className={`text-xs font-semibold ${level.colorAccent}`}>{level.credential}</span>
                            </div>
                            <span className="text-[10px] text-white/30">Competition: {level.competition}</span>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <a href="#competitions">
                              <Button size="sm" className={`bg-gradient-to-r ${level.color} text-white font-bold rounded-full px-5`} data-testid={`enroll-${level.id}-btn`}>
                                Choose a Track <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                              </Button>
                            </a>
                            <a href="#courses">
                              <Button size="sm" variant="outline" className="border-white/15 text-white/60 hover:bg-white/5 rounded-full px-5">View Courses</Button>
                            </a>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══════════ COMPETITION TRACKS — FIRST vs VEX (parallel) ═══════════ */}
      <section id="competitions" className="relative py-12 bg-gradient-to-b from-rsc-navy to-[#0f2847]" data-testid="competition-tracks-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rsc-yellow/30 bg-rsc-yellow/10 text-rsc-yellow text-xs font-medium mb-3">
              <Trophy className="w-3.5 h-3.5" /> CHOOSE YOUR TRACK
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white mb-2">
              Competition <span className="text-rsc-yellow">Tracks</span>
            </h2>
            <p className="text-white/50 max-w-lg mx-auto text-sm">
              FIRST and VEX are parallel ecosystems — learners typically commit to one track. Both offer world-class competition, scholarships, and skill-building.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* ── FIRST Column ── */}
            <div className="rounded-xl border border-red-500/20 bg-red-500/[0.03] overflow-hidden" data-testid="first-track">
              <div className="h-1 bg-gradient-to-r from-red-500 to-rose-400" />
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-500/15 flex items-center justify-center">
                    <Bot className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">FIRST Robotics</h3>
                    <p className="text-xs text-red-400/80">For Inspiration and Recognition of Science and Technology</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* FLL Explore */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">FLL Explore</h4>
                      <Badge className="bg-red-500/10 text-red-300 border-red-400/20 text-[10px]">Grades K–4</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Introduce young learners to STEM through LEGO-based guided builds. Team events (not competitive judging).</p>
                    <div className="flex gap-1.5">
                      {['LEGO WeDo', 'Team Events', 'Innovation'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* FLL Challenge */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">FLL Challenge</h4>
                      <Badge className="bg-red-500/10 text-red-300 border-red-400/20 text-[10px]">Grades 4–8</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Design, build, and program LEGO SPIKE robots. Solve real-world problems. Teams compete in missions + innovation project.</p>
                    <div className="flex gap-1.5">
                      {['SPIKE Prime', 'Missions', 'Innovation Project', 'Core Values'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* FTC */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">FTC (FIRST Tech Challenge)</h4>
                      <Badge className="bg-red-500/10 text-red-300 border-red-400/20 text-[10px]">Grades 7–12</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Reusable robot kit systems (REV/goBILDA). 2v2 alliance-based matches on a 12ft field. Java or Blocks programming.</p>
                    <div className="flex gap-1.5">
                      {['Java/Blocks', 'REV/goBILDA', '2v2 Alliance', 'Engineering Notebook'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* FRC */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">FRC (FIRST Robotics Competition)</h4>
                      <Badge className="bg-red-500/10 text-red-300 border-red-400/20 text-[10px]">Grades 9–12</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">The pinnacle — 125lb robots, 6-week build season, alliance tournaments. 3,600+ teams globally. $80M+ in scholarships.</p>
                    <div className="flex gap-1.5">
                      {['125lb Robots', 'CAD/CNC', '$80M Scholarships', 'Regionals'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  {activeForm !== 'first' ? (
                    <Button onClick={() => setActiveForm('first')} className="w-full bg-red-500/15 text-red-400 hover:bg-red-500/25 rounded-full text-sm" data-testid="first-interest-btn">
                      <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> I'm Interested in FIRST
                    </Button>
                  ) : <InlineContactForm program="FIRST" />}
                </div>
              </div>
            </div>

            {/* ── VEX Column ── */}
            <div className="rounded-xl border border-green-500/20 bg-green-500/[0.03] overflow-hidden" data-testid="vex-track">
              <div className="h-1 bg-gradient-to-r from-green-500 to-emerald-400" />
              <div className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-500/15 flex items-center justify-center">
                    <Cpu className="w-5 h-5 text-green-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">VEX Robotics</h3>
                    <p className="text-xs text-green-400/80">VEX Robotics Competition Ecosystem</p>
                  </div>
                </div>

                <div className="space-y-3">
                  {/* VEX GO */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">VEX GO</h4>
                      <Badge className="bg-green-500/10 text-green-300 border-green-400/20 text-[10px]">Grades K–5</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Snap-together building system for younger learners. Drag-and-drop coding with VEXcode. Classroom-friendly competitions.</p>
                    <div className="flex gap-1.5">
                      {['Snap-Together', 'VEXcode', 'Classroom Competitions'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* VEX IQ */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">VEX IQ</h4>
                      <Badge className="bg-green-500/10 text-green-300 border-green-400/20 text-[10px]">Grades 3–8</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Plastic snap-together kits with sensors. Teamwork & Driver/Skills challenges. VEXcode or Python programming.</p>
                    <div className="flex gap-1.5">
                      {['VEX IQ Kit', 'Teamwork Challenge', 'Skills Challenge', 'VEXcode/Python'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* VEX V5 */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">VEX V5 (VRC)</h4>
                      <Badge className="bg-green-500/10 text-green-300 border-green-400/20 text-[10px]">Grades 6–12</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">Metal chassis with V5 smart motors. Advanced sensors & odometry. C++/Python. Alliance-based tournament play.</p>
                    <div className="flex gap-1.5">
                      {['Metal Chassis', 'V5 Motors', 'C++/Python', 'Alliance Tournaments'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>

                  {/* VEX U */}
                  <div className="p-3 rounded-lg bg-white/[0.03] border border-white/[0.06]">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="text-sm font-semibold text-white">VEX U</h4>
                      <Badge className="bg-green-500/10 text-green-300 border-green-400/20 text-[10px]">Post-Secondary</Badge>
                    </div>
                    <p className="text-xs text-white/40 mb-2">University-level competition. Custom 3D-printed parts allowed. Two robots per alliance. Advanced autonomous routines.</p>
                    <div className="flex gap-1.5">
                      {['University Teams', 'Custom Parts', '2 Robots/Alliance', 'Advanced Auto'].map(t => <span key={t} className="text-[9px] text-white/25 bg-white/[0.04] px-1.5 py-0.5 rounded-full">{t}</span>)}
                    </div>
                  </div>
                </div>

                <div className="mt-4">
                  {activeForm !== 'vex' ? (
                    <Button onClick={() => setActiveForm('vex')} className="w-full bg-green-500/15 text-green-400 hover:bg-green-500/25 rounded-full text-sm" data-testid="vex-interest-btn">
                      <MessageSquare className="w-3.5 h-3.5 mr-1.5" /> I'm Interested in VEX
                    </Button>
                  ) : <InlineContactForm program="VEX" />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FIRST SCHOLARSHIPS ═══════════ */}
      <section className="relative py-12 bg-gradient-to-b from-[#0f2847] to-[#0f2847]" data-testid="first-scholarships-section">
        <div className="container-app">
          <div className="max-w-3xl mx-auto">
            <div className="p-6 rounded-xl bg-rsc-yellow/10 border border-rsc-yellow/20">
              <div className="flex items-start gap-4">
                <Trophy className="w-8 h-8 text-rsc-yellow flex-shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-bold text-white mb-2">$80M+ in FIRST Scholarships Annually</h2>
                  <p className="text-sm text-white/70 mb-3">
                    FRC and FTC participants gain access to exclusive scholarship pools from 200+ colleges and universities across the United States.
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-rsc-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-white/60">Merit-based scholarships ranging from $1,000 to full tuition</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-rsc-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-white/60">Dedicated FIRST participant application pools</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-rsc-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-white/60">Engineering programs, business schools, and liberal arts colleges</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-rsc-yellow flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-white/60">Recognition on college applications and resumes</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FANUC ADVANTAGE ═══════════ */}
      <section className="relative py-12 bg-[#0f2847]" data-testid="advantage-section">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-orange-400/30 bg-orange-500/10 text-orange-300 text-xs font-medium mb-3">
                <Shield className="w-3.5 h-3.5" /> UNFAIR ADVANTAGE
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
                7 FANUC Industrial Robots.<br /><span className="text-orange-400">No One Else Has This.</span>
              </h2>
              <p className="text-sm text-white/50 mb-5 leading-relaxed">
                Fewer than 5% of K-12 programs nationally have access to even one industrial robot.
                We have six — classroom-scale instruction and a direct path to
                <span className="text-white/70 font-semibold"> FANUC Authorized Satellite Training (FAST)</span> certification.
              </p>
              <div className="space-y-2">
                {[
                  { cert: 'Handling Tool Operation', target: 'HS Juniors/Seniors', status: 'Available Now' },
                  { cert: 'iRVision 2D/3D', target: 'Post-Secondary', status: 'Coming 2027' },
                  { cert: 'Multi-Process (Weld/Paint)', target: 'Post-Secondary', status: 'Coming 2027' },
                  { cert: 'ROBOGUIDE Simulation', target: 'Advanced', status: 'Coming 2028' },
                ].map((c, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-white/[0.04] border border-white/[0.06]">
                    <div className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-orange-400" />
                      <div>
                        <p className="text-sm font-medium text-white">{c.cert}</p>
                        <p className="text-[10px] text-white/35">{c.target}</p>
                      </div>
                    </div>
                    <Badge className={`text-[10px] ${c.status === 'Available Now' ? 'bg-emerald-500/15 text-emerald-300 border-emerald-400/20' : 'bg-white/5 text-white/35 border-white/10'}`}>{c.status}</Badge>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-4">
              <div className="relative rounded-xl overflow-hidden aspect-[4/3]">
                <img src="https://images.unsplash.com/photo-1647427060118-4911c9821b82?w=700&auto=format" alt="Industrial robotics lab" className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0f2847] via-[#0f2847]/20 to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-white font-bold text-sm">FANUC Industrial Robotics Lab</p>
                  <p className="text-white/50 text-xs">6 stations — pick-and-place, palletizing, vision, advanced applications</p>
                </div>
              </div>
              {/* Industrial CTA */}
              <a href="https://www.apstrainingacademy.org/adult-courses/" target="_blank" rel="noopener noreferrer" className="block">
                <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20 hover:bg-blue-500/15 transition-colors cursor-pointer">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Factory className="w-5 h-5 text-blue-400 flex-shrink-0" />
                      <div>
                        <h3 className="text-sm font-bold text-white">Adult Industrial Robotics Training</h3>
                        <p className="text-xs text-white/50">FANUC certified courses via APS Training Academy</p>
                      </div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-blue-400" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PARTNER WITH US ═══════════ */}
      <section className="relative py-12 bg-gradient-to-b from-[#0f2847] to-rsc-navy" data-testid="partner-section">
        <div className="container-app">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/5 text-white/50 text-xs font-medium mb-3">
              <Handshake className="w-3.5 h-3.5" /> INDUSTRY PARTNERSHIPS
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
              Partner With <span className="text-rsc-yellow">Ready Set STEM</span>
            </h2>
            <p className="text-white/45 max-w-lg mx-auto text-sm">
              Your investment connects students to careers and your company to a pipeline of trained, certified talent.
            </p>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            {[
              { tier: 'Platinum', investment: '$25K+ / year', accent: 'bg-white/20 border-white/30', examples: 'Major manufacturers, FANUC, universities' },
              { tier: 'Gold', investment: '$10K–$25K / year', accent: 'bg-rsc-yellow/20 border-rsc-yellow/30', examples: 'Regional manufacturers, logistics, tech' },
              { tier: 'Silver', investment: '$5K–$10K / year', accent: 'bg-white/10 border-white/15', examples: 'Small-medium businesses, associations' },
              { tier: 'Community', investment: 'In-kind / Volunteering', accent: 'bg-emerald-500/10 border-emerald-400/20', examples: 'Chamber of commerce, civic orgs, media' },
            ].map((tier, i) => (
              <div key={i} className={`p-4 rounded-xl border ${tier.accent}`} data-testid={`partner-tier-${tier.tier.toLowerCase()}`}>
                <h3 className="text-sm font-bold text-white mb-0.5">{tier.tier}</h3>
                <p className="text-rsc-yellow font-semibold text-xs mb-1.5">{tier.investment}</p>
                <p className="text-[10px] text-white/35">{tier.examples}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div>
              <h3 className="text-sm font-bold text-white mb-2">Target Partner Categories</h3>
              <div className="space-y-1.5">
                {[
                  { icon: Factory, label: 'Advanced Manufacturing', desc: 'Caterpillar, John Deere, Illinois Tool Works' },
                  { icon: Globe, label: 'Logistics & Warehousing', desc: 'Amazon, FedEx, UPS, regional 3PL' },
                  { icon: Cpu, label: 'Technology', desc: 'Motorola Solutions, Argonne, Fermilab' },
                  { icon: GraduationCap, label: 'Higher Education', desc: 'Waubonsee CC, NIU, U of I system' },
                ].map((p, i) => (
                  <div key={i} className="flex items-start gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/[0.04]">
                    <p.icon className="w-3.5 h-3.5 text-rsc-yellow mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-semibold text-white">{p.label}</p>
                      <p className="text-[10px] text-white/30">{p.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 rounded-xl border border-white/[0.08] bg-white/[0.03]">
              <h3 className="text-sm font-bold text-white mb-1">Become a Partner</h3>
              <p className="text-white/40 text-xs mb-3">Interested in sponsoring or mentoring?</p>
              <InlineContactForm program="Partnership" />
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ BOTTOM CTA ═══════════ */}
      <section className="relative py-12 bg-rsc-navy">
        <div className="container-app text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">The future needs builders.</h2>
          <p className="text-sm text-white/45 mb-5 max-w-md mx-auto">Start your robotics journey today — from first builds to industry certifications.</p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href="#courses"><Button className="bg-rsc-yellow text-rsc-navy text-sm px-8 py-5 font-bold rounded-full shadow-lg shadow-rsc-yellow/20" data-testid="bottom-cta-courses">Explore Courses <ArrowRight className="ml-2 h-4 w-4" /></Button></a>
            <a href="#competitions"><Button variant="outline" className="border-white/20 text-white hover:bg-white/5 text-sm px-8 py-5 rounded-full" data-testid="bottom-cta-tracks">Choose a Track</Button></a>
          </div>
        </div>
      </section>
      <CourseDetailModal state={courseModal.state} onClose={courseModal.close} onRegister={(course) => window.location.href = `/summer-camps?register=${encodeURIComponent(course.title)}`} />
    </div>
  );
}
